import * as React from "react";
import {
    Badge,
    Banner,
    Button,
    Card,
    CardBody,
    CardHeader,
    Checkbox,
    ConfirmModal,
    DetailField,
    DetailSection,
    EmptyState,
    Field,
    FilterPill,
    IconButton,
    IdBadge,
    Input,
    LoadingState,
    Modal,
    ModalBody,
    PageContainer,
    PageHeader,
    SearchInput,
    Select,
    StatusBadge,
    Table,
    Tbody,
    Td,
    TdActions,
    Textarea,
    Th,
    Thead,
    Toolbar,
    Tr,
} from ".";
import { useToast } from "../general/Toast";

/**
 * A live gallery of every primitive, routed at /dev/components.
 *
 * ScadsysIdentity has the same idea in components/ModalExamples.tsx. It earns its place here for one
 * specific reason: this app has a dark theme and the reference does not, so every component has to be
 * checked in both. Toggling the theme on this one page exercises the whole system at once, which is
 * far more reliable than remembering to re-check twenty feature screens.
 */

const TONES = ["neutral", "primary", "success", "warning", "danger", "info"] as const;

export const Gallery: React.FC = () => {
    const toast = useToast();
    const [modalOpen, setModalOpen] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const [filter, setFilter] = React.useState(true);

    return (
        <PageContainer>
            <PageHeader
                title="Component gallery"
                icon={["fad", "swatchbook"]}
                description="Every primitive in the design system. Toggle the theme to check both palettes at once."
                actions={<Button icon={["fad", "wand-magic-sparkles"]}>Primary action</Button>}
            />

            <Card className="mb-6">
                <CardHeader>
                    <h3 className="text-base font-semibold text-text-strong">Buttons</h3>
                </CardHeader>
                <CardBody>
                    <div className="mb-3 flex flex-wrap gap-2">
                        <Button variant="primary">Primary</Button>
                        <Button variant="secondary">Secondary</Button>
                        <Button variant="danger">Danger</Button>
                        <Button variant="success">Success</Button>
                        <Button variant="warning">Warning</Button>
                        <Button variant="info">Info</Button>
                    </div>
                    <div className="mb-3 flex flex-wrap gap-2">
                        <Button variant="outline-primary">Outline primary</Button>
                        <Button variant="outline-secondary">Outline secondary</Button>
                        <Button variant="outline-danger">Outline danger</Button>
                        <Button variant="outline-success">Outline success</Button>
                        <Button variant="ghost">Ghost</Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm">Small</Button>
                        <Button size="md">Medium</Button>
                        <Button size="lg">Large</Button>
                        <Button loading>Loading</Button>
                        <Button disabled>Disabled</Button>
                        <Button icon={["fad", "plus"]}>With icon</Button>
                        <IconButton icon={["fad", "pen-to-square"]} label="Edit" />
                        <IconButton icon={["fad", "trash"]} variant="outline-danger" label="Delete" />
                    </div>
                </CardBody>
            </Card>

            <Card className="mb-6">
                <CardHeader>
                    <h3 className="text-base font-semibold text-text-strong">Form fields</h3>
                </CardHeader>
                <CardBody>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Text" required hint="A short hint under the field.">
                            <Input placeholder="Term Mark" />
                        </Field>
                        <Field label="With error">
                            <Input defaultValue="Not valid" error="That name is already in use." />
                        </Field>
                        <Field label="Select">
                            <Select placeholder="Choose a method">
                                <option value="SimpleAverage">Simple average</option>
                                <option value="WeightedAverage">Weighted average</option>
                                <option value="Aggregate">Aggregate</option>
                            </Select>
                        </Field>
                        <Field label="Textarea">
                            <Textarea placeholder="Moderation comment" />
                        </Field>
                    </div>
                    <Checkbox label="Save results" hint="Unticked runs a preview without writing anything." defaultChecked />
                </CardBody>
            </Card>

            <Card className="mb-6">
                <CardHeader>
                    <h3 className="text-base font-semibold text-text-strong">Toolbar and table</h3>
                </CardHeader>
                <CardBody className="p-0">
                    <div className="p-6 pb-0">
                        <Toolbar>
                            <SearchInput value={query} onChange={setQuery} />
                            <FilterPill label="Active only" active={filter} onToggle={() => setFilter(!filter)} icon={["fad", "filter"]} />
                        </Toolbar>
                    </div>
                    <Table>
                        <Thead>
                            <Tr>
                                <Th>Student</Th>
                                <Th>Identifier</Th>
                                <Th align="right">Result</Th>
                                <Th>Status</Th>
                                <Th align="right">Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {[
                                { name: "Sample learner", id: "01J8QK3M4N5P6Q7R8S9T0UVWXY", result: "72 / 100", status: "Finalized" },
                                { name: "Another learner", id: "01J8QK3M4N5P6Q7R8S9T0ABCDE", result: "58 / 100", status: "Pending" },
                                { name: "Third learner", id: "01J8QK3M4N5P6Q7R8S9T0FGHIJ", result: "91 / 100", status: "Rejected" },
                            ].map((row) => (
                                <Tr key={row.id}>
                                    <Td>{row.name}</Td>
                                    <Td>
                                        <IdBadge id={row.id} />
                                    </Td>
                                    <Td align="right">{row.result}</Td>
                                    <Td>
                                        <StatusBadge status={row.status} />
                                    </Td>
                                    <TdActions>
                                        <IconButton icon={["fad", "pen-to-square"]} label="Edit" />
                                        <IconButton icon={["fad", "trash"]} variant="outline-danger" label="Delete" />
                                    </TdActions>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </CardBody>
            </Card>

            <Card className="mb-6">
                <CardHeader>
                    <h3 className="text-base font-semibold text-text-strong">Badges and status</h3>
                </CardHeader>
                <CardBody>
                    <div className="mb-3 flex flex-wrap gap-2">
                        {TONES.map((tone) => (
                            <Badge key={tone} tone={tone}>
                                {tone}
                            </Badge>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {["Open", "Finalized", "Pending", "Approved", "Rejected", "Manual", "Calculated", "Draft", "Published", "Blocked"].map(
                            (s) => (
                                <StatusBadge key={s} status={s} />
                            )
                        )}
                    </div>
                </CardBody>
            </Card>

            <Card className="mb-6">
                <CardHeader>
                    <h3 className="text-base font-semibold text-text-strong">Messages and states</h3>
                </CardHeader>
                <CardBody>
                    <div className="mb-4 space-y-2">
                        <Banner tone="info">A neutral note about how this screen behaves.</Banner>
                        <Banner tone="success">Calculation run saved 42 results.</Banner>
                        <Banner tone="warning">Two students had no mark for one component.</Banner>
                        <Banner tone="danger">The request failed. Check the assignment is active.</Banner>
                    </div>

                    <div className="mb-4 flex flex-wrap gap-2">
                        <Button variant="outline-secondary" onClick={() => toast.success("Saved.")}>
                            Toast success
                        </Button>
                        <Button variant="outline-secondary" onClick={() => toast.info("Nothing changed.")}>
                            Toast info
                        </Button>
                        <Button variant="outline-secondary" onClick={() => toast.warning("Some marks were skipped.")}>
                            Toast warning
                        </Button>
                        <Button variant="outline-danger" onClick={() => toast.error("That did not work.")}>
                            Toast error
                        </Button>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="rounded-card border border-border">
                            <LoadingState />
                        </div>
                        <div className="rounded-card border border-border">
                            <EmptyState
                                icon={["fad", "inbox"]}
                                title="No mark types yet"
                                hint="A chained procedure needs one mark type per level."
                                action={<Button size="sm">Add mark type</Button>}
                            />
                        </div>
                    </div>
                </CardBody>
            </Card>

            <Card className="mb-6">
                <CardHeader>
                    <h3 className="text-base font-semibold text-text-strong">Detail fields</h3>
                </CardHeader>
                <CardBody>
                    <DetailSection title="Procedure version">
                        <DetailField label="Method">Weighted average</DetailField>
                        <DetailField label="Missing marks">Ignore missing</DetailField>
                        <DetailField label="Target mark type">Term Mark</DetailField>
                        <DetailField label="Grade scale">(none)</DetailField>
                        <DetailField label="Version id" mono>
                            01J8QK3M4N5P6Q7R8S9T0UVWXY
                        </DetailField>
                        <DetailField label="Not set" />
                    </DetailSection>
                </CardBody>
            </Card>

            <Card>
                <CardHeader>
                    <h3 className="text-base font-semibold text-text-strong">Modals</h3>
                </CardHeader>
                <CardBody>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline-secondary" onClick={() => setModalOpen(true)}>
                            Open modal
                        </Button>
                        <Button variant="outline-danger" onClick={() => setConfirmOpen(true)}>
                            Open confirm
                        </Button>
                    </div>
                </CardBody>
            </Card>

            <Modal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title="Example modal"
                icon={["fad", "circle-info"]}
                footer={
                    <>
                        <Button variant="outline-secondary" onClick={() => setModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={() => setModalOpen(false)}>Save</Button>
                    </>
                }
            >
                <ModalBody>
                    <p className="text-sm text-text">
                        Portalled, closes on Escape, restores focus to whatever opened it, and locks background scroll.
                    </p>
                </ModalBody>
            </Modal>

            <ConfirmModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={(comment) => {
                    toast.warning(comment ? `Rejected: ${comment}` : "Rejected.");
                    setConfirmOpen(false);
                }}
                title="Reject marks"
                confirmLabel="Reject"
                requireComment
                commentLabel="Reason"
                commentPlaceholder="Why are these marks being sent back?"
                body="The server refuses a blank comment on a reject, so the button stays disabled until one is typed."
            />
        </PageContainer>
    );
};

export default Gallery;
